import { useState, useEffect, useRef, useCallback } from 'react';
import { globalCampaignAPI, mailListAPI, templateLibAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  Plus, Loader2, CheckCircle, XCircle, Play, Square, ArrowLeft, Upload,
  Zap, RefreshCw, Trash2, Users, Mail, BarChart3, Shield, Clock, Gauge,
} from 'lucide-react';
import { toast } from 'sonner';

const formatETA = (secs) => {
  if (secs == null || secs < 0) return '—';
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  if (h > 0) return `${h}s ${m}dk`;
  if (m > 0) return `${m}dk ${s}sn`;
  return `${s}sn`;
};

// === LIST VIEW ===
function GCListView({ onSelect, onCreate, onLists }) {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { globalCampaignAPI.list().then(r => setCampaigns(r.data)).finally(() => setLoading(false)); }, []);

  const statusBadge = (s) => {
    const m = { created: 'Hazir', running: 'Gonderiliyor', completed: 'Tamamlandi', stopped: 'Durduruldu', failed: 'Basarisiz' };
    const c = { created: 'secondary', running: 'default', completed: 'default', stopped: 'destructive', failed: 'destructive' };
    return <Badge variant={c[s] || 'outline'}>{m[s] || s}</Badge>;
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div>;
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div><h1 className="text-2xl font-heading font-bold">Global Mail Kampanyalari</h1><p className="text-muted-foreground mt-1">Round Robin multi-domain gonderim</p></div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={onLists}><Users className="w-4 h-4 mr-1" /> Mail Listeleri</Button>
          <Button size="sm" onClick={onCreate}><Plus className="w-4 h-4 mr-1" /> Kampanya Olustur</Button>
        </div>
      </div>
      {campaigns.length === 0 ? (
        <Card><CardContent className="flex flex-col items-center py-16"><Mail className="w-12 h-12 text-muted-foreground/30 mb-4" /><p className="text-muted-foreground">Henuz kampanya yok</p><Button className="mt-4" onClick={onCreate}><Plus className="w-4 h-4 mr-1" /> Olustur</Button></CardContent></Card>
      ) : (
        <div className="space-y-3">{campaigns.map(c => (
          <Card key={c.id} className="cursor-pointer hover:border-primary/40 transition-colors" onClick={() => onSelect(c.id)}>
            <CardContent className="flex items-center justify-between py-4">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center"><Zap className="w-5 h-5 text-purple-500" /></div>
                <div>
                  <p className="font-medium">{c.name}</p>
                  <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                    <span>{c.planned_count} hedef</span>
                    <span className="text-green-500">{c.sent_count} gonderildi</span>
                    {c.suppressed_count > 0 && <span className="text-amber-500">{c.suppressed_count} engellendi</span>}
                    {c.failed_count > 0 && <span className="text-red-400">{c.failed_count} basarisiz</span>}
                  </div>
                </div>
              </div>
              {statusBadge(c.status)}
            </CardContent>
          </Card>
        ))}</div>
      )}
    </div>
  );
}

// === CREATE VIEW ===
function GCCreateView({ onCreated, onBack }) {
  const [name, setName] = useState('');
  const [mode, setMode] = useState('reputation_safe');
  const [warmup, setWarmup] = useState(false);
  const [sendRate, setSendRate] = useState(6);
  const [parallelCount, setParallelCount] = useState(1);
  const [inputMode, setInputMode] = useState('manual');
  const [manualEmails, setManualEmails] = useState('');
  const [lists, setLists] = useState([]);
  const [selectedList, setSelectedList] = useState('');
  const [csvFile, setCsvFile] = useState(null);
  const [csvName, setCsvName] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [creating, setCreating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [contentMode, setContentMode] = useState('template'); // 'template' or 'manual'
  const [activeTemplates, setActiveTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  useEffect(() => {
    mailListAPI.list().then(r => setLists(r.data)).catch(() => {});
    templateLibAPI.list().then(r => {
      const active = (r.data || []).filter(t => t.status === 'active');
      setActiveTemplates(active);
    }).catch(() => {});
  }, []);

  const handleSelectTemplate = async (t) => {
    try {
      const res = await templateLibAPI.get(t.id);
      const full = res.data;
      setSelectedTemplate(full);
      setSubject(full.subject);
      setBody(full.body_html);
    } catch (e) { toast.error('Template yuklenemedi'); }
  };

  const handleCSVUpload = async () => {
    if (!csvFile || !csvName) return toast.error('CSV dosyasi ve liste adi gerekli');
    setUploading(true);
    try {
      const res = await mailListAPI.importCSV(csvFile, csvName);
      toast.success(`${res.data.imported_count} kisi import edildi`);
      const listsRes = await mailListAPI.list();
      setLists(listsRes.data);
      setSelectedList(res.data.list_id);
      setInputMode('list');
    } catch (e) { toast.error('Import hatasi'); }
    setUploading(false);
  };

  const handleCreate = async () => {
    if (!name.trim() || !subject.trim() || !body.trim()) return toast.error('Ad, konu ve icerik zorunlu');
    let data = { name, templates: [{ subject, body }], mode, warmup_mode: warmup, send_rate_per_minute: sendRate, parallel_count: parallelCount };

    if (inputMode === 'list' && selectedList) {
      data.list_id = selectedList;
    } else {
      const emails = manualEmails.split('\n').map(e => e.trim()).filter(e => e.includes('@'));
      if (emails.length === 0) return toast.error('En az 1 email girin');
      data.recipients = emails;
    }

    setCreating(true);
    try {
      const res = await globalCampaignAPI.create(data);
      const v = res.data.validation || {};
      const removed = (v.invalid_removed || 0) + (v.duplicate_removed || 0) + (v.suppressed_removed || 0);
      if (removed > 0) {
        const lines = [];
        if (v.invalid_removed > 0) lines.push(`${v.invalid_removed.toLocaleString('tr-TR')} geçersiz format`);
        if (v.duplicate_removed > 0) lines.push(`${v.duplicate_removed.toLocaleString('tr-TR')} duplicate`);
        if (v.suppressed_removed > 0) lines.push(`${v.suppressed_removed.toLocaleString('tr-TR')} suppression listesinde`);
        toast.success(
          `Kampanya oluşturuldu — ${(v.final || res.data.planned || 0).toLocaleString('tr-TR')} email gönderilecek\n` +
          `Kaldırılan: ${lines.join(', ')}`,
          { duration: 6000 }
        );
      } else {
        toast.success(`Kampanya oluşturuldu: ${(res.data.planned || 0).toLocaleString('tr-TR')} hedef`);
      }
      onCreated(res.data.id);
    } catch (e) { toast.error(e.response?.data?.detail || 'Olusturulamadi'); }
    setCreating(false);
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="flex items-center gap-3"><Button variant="ghost" size="icon" onClick={onBack}><ArrowLeft className="w-5 h-5" /></Button><h1 className="text-2xl font-heading font-bold">Kampanya Olustur</h1></div>

      <Card><CardHeader><CardTitle>Kampanya Bilgileri</CardTitle></CardHeader><CardContent className="space-y-4">
        <div><Label>Kampanya Adi</Label><Input value={name} onChange={e => setName(e.target.value)} placeholder="Orn: Mayis Tanitim" /></div>
        <div className="flex gap-3">
          <div><Label>Mod</Label><div className="flex gap-1 mt-1">{['reputation_safe','stealth'].map(m => <button key={m} onClick={() => setMode(m)} className={`px-3 py-1 rounded text-xs ${mode===m?'bg-primary text-primary-foreground':'bg-muted'}`}>{m==='reputation_safe'?'Guvenli':'Stealth'}</button>)}</div></div>
          <div><Label>Warmup Mode</Label>
            <button onClick={() => setWarmup(!warmup)} className={`mt-1 px-3 py-1 rounded text-xs ${warmup?'bg-red-500 text-white':'bg-muted'}`}>{warmup?'ACIK (her domain x her alici)':'Kapali (normal)'}</button>
            {warmup && <p className="text-[10px] text-red-400 mt-1">DIKKAT: Her domain her aliciya ayri mail gonderir!</p>}
          </div>
        </div>
        <div>
          <Label>Gonderim Hizi</Label>
          <div className="flex gap-1 mt-1 flex-wrap">
            {[
              {rate: 6, label: 'Yavas', desc: 'Warmup'},
              {rate: 60, label: 'Normal', desc: 'Standart'},
              {rate: 200, label: 'Hizli', desc: 'Saglikli domain'},
              {rate: 400, label: 'Turbo', desc: 'Isinmis domain'},
              {rate: 600, label: 'Maksimum', desc: 'Tam guc'},
            ].map(s => (
              <button key={s.rate} onClick={() => setSendRate(s.rate)}
                className={`px-3 py-1.5 rounded text-xs ${sendRate===s.rate ? s.rate >= 400 ? 'bg-red-500 text-white' : s.rate >= 200 ? 'bg-amber-500 text-white' : 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}>
                {s.label} ({s.rate}/dk)
              </button>
            ))}
          </div>
          <p className="text-[10px] text-muted-foreground mt-1">
            {sendRate <= 6 ? 'Warmup icin guvenli hiz' : sendRate <= 60 ? 'Normal gonderim' : sendRate <= 200 ? 'Hizli gonderim' : sendRate <= 400 ? 'Turbo mod — ThreadPool paralel' : 'Maksimum guc — 31 domain tam paralel'}
            {' | ~'}{Math.round(sendRate * 60).toLocaleString()} mail/saat
          </p>
        </div>
        <div>
          <Label>Paralel Gonderim</Label>
          <div className="flex gap-1 mt-1">
            {[1, 5, 10, 20, 31].map(n => (
              <button key={n} onClick={() => setParallelCount(n)}
                className={`px-4 py-1.5 rounded text-xs font-medium ${parallelCount===n ? n >= 20 ? 'bg-red-500 text-white' : n >= 10 ? 'bg-amber-500 text-white' : 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}>
                {n}x
              </button>
            ))}
          </div>
          <p className="text-[10px] text-muted-foreground mt-1">
            {parallelCount === 1 ? 'Sirayla tek tek gonderim (en guvenli)' : `${parallelCount} thread ayni anda gonderir (gercek paralel)`}
            {' | ~'}{Math.round(sendRate * 60).toLocaleString()} mail/saat
          </p>
        </div>
      </CardContent></Card>

      {/* Email Icerigi — Template Sec veya Manuel */}
      <Card><CardHeader><CardTitle>Email Icerigi</CardTitle></CardHeader><CardContent className="space-y-4">
        <div className="flex gap-1">
          <button onClick={() => setContentMode('template')} className={`px-3 py-1.5 rounded text-xs font-medium ${contentMode==='template'?'bg-primary text-primary-foreground':'bg-muted text-muted-foreground'}`}>Template Sec</button>
          <button onClick={() => setContentMode('manual')} className={`px-3 py-1.5 rounded text-xs font-medium ${contentMode==='manual'?'bg-primary text-primary-foreground':'bg-muted text-muted-foreground'}`}>Manuel Yaz</button>
        </div>

        {contentMode === 'template' && (
          <div className="space-y-3">
            {activeTemplates.length === 0 ? (
              <p className="text-sm text-muted-foreground">Aktif template yok. Template Builder dan olusturun ve aktif edin.</p>
            ) : (
              <div className="space-y-2">
                {activeTemplates.map(t => (
                  <div key={t.id} onClick={() => handleSelectTemplate(t)}
                    className={`p-3 rounded-lg border cursor-pointer transition-all ${selectedTemplate?.id === t.id ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/50'}`}>
                    <div className="flex items-center justify-between">
                      <p className="font-medium text-sm">{t.name}</p>
                      <Badge className="bg-green-500/20 text-green-400 text-[10px]">Aktif</Badge>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">Subject: {t.subject}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {contentMode === 'manual' && (
          <div className="space-y-3">
            <div><Label>Konu (Subject)</Label><Input value={subject} onChange={e => setSubject(e.target.value)} placeholder="Email konusu" /></div>
            <div><Label>Icerik (HTML)</Label><textarea className="flex min-h-[120px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono" value={body} onChange={e => setBody(e.target.value)} placeholder="<p>Merhaba...</p>" /></div>
          </div>
        )}

        {/* Template Preview */}
        {body && (
          <div>
            <Label className="text-muted-foreground">Mail Onizleme</Label>
            <p className="text-[10px] text-amber-400 mt-1 mb-2">Onizleme ornektir. Gercek gonderimde her domain kendi brand adi ve linkini kullanir.</p>
            <div className="border border-border rounded-lg overflow-hidden mt-1">
              <div className="bg-muted/30 px-4 py-2 text-xs text-muted-foreground">Subject: {subject} <span className="text-amber-400 ml-2">({'{{brand_name}}'} → domain adina gore degisir)</span></div>
              <div className="p-1 max-h-[400px] overflow-auto" dangerouslySetInnerHTML={{ __html: body.replace(/\{\{brand_name\}\}/g, 'Artemisbet').replace(/\{\{domain_url\}\}/g, 'https://artemisbetgirisitesi.com').replace(/\{\{unsubscribe_url\}\}/g, '#') }} />
            </div>
          </div>
        )}
      </CardContent></Card>

      <Card><CardHeader><CardTitle>Alicilar</CardTitle></CardHeader><CardContent className="space-y-4">
        <div className="flex gap-1">{[{k:'manual',l:'Manuel'},{k:'list',l:'Listeden'},{k:'csv',l:'CSV Yukle'}].map(t => <button key={t.k} onClick={() => setInputMode(t.k)} className={`px-3 py-1.5 rounded text-xs font-medium ${inputMode===t.k?'bg-primary text-primary-foreground':'bg-muted text-muted-foreground'}`}>{t.l}</button>)}</div>

        {inputMode === 'manual' && <textarea className="flex min-h-[120px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono" value={manualEmails} onChange={e => setManualEmails(e.target.value)} placeholder="her satira bir email" />}

        {inputMode === 'list' && (
          <div className="space-y-2">{lists.length === 0 ? <p className="text-sm text-muted-foreground">Henuz liste yok. CSV yukleyin.</p> :
            lists.map(l => <div key={l.id} onClick={() => setSelectedList(l.id)} className={`p-3 rounded-lg border cursor-pointer ${selectedList===l.id?'border-primary bg-primary/5':'border-border hover:bg-muted/50'}`}>
              <p className="text-sm font-medium">{l.name}</p>
              <p className="text-xs text-muted-foreground">{l.total_count} kisi | {l.valid_count} gecerli | {l.suppressed_count} engelli</p>
            </div>)
          }</div>
        )}

        {inputMode === 'csv' && (
          <div className="space-y-3">
            <div><Label>Liste Adi</Label><Input value={csvName} onChange={e => setCsvName(e.target.value)} placeholder="Orn: Mayis Listesi" /></div>
            <div><Label>CSV Dosyasi</Label><Input type="file" accept=".csv" onChange={e => setCsvFile(e.target.files?.[0])} /></div>
            <Button variant="outline" onClick={handleCSVUpload} disabled={uploading}>{uploading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />} Import Et</Button>
          </div>
        )}
      </CardContent></Card>

      <Button className="w-full" size="lg" onClick={handleCreate} disabled={creating}>{creating ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Zap className="w-5 h-5 mr-2" />} Kampanya Olustur</Button>
    </div>
  );
}

// === DETAIL VIEW ===
function GCDetailView({ campaignId, onBack }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const pollingRef = useRef(null);

  const load = useCallback(async () => {
    try { const r = await globalCampaignAPI.get(campaignId); setData(r.data); setLoading(false);
      if (['completed','stopped','failed'].includes(r.data.status) && pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; }
    } catch (e) { setLoading(false); }
  }, [campaignId]);

  useEffect(() => { load(); pollingRef.current = setInterval(load, 3000); return () => { if (pollingRef.current) clearInterval(pollingRef.current); }; }, [load]);

  const handleStart = async () => { try { await globalCampaignAPI.start(campaignId); toast.success('Baslatildi'); if (!pollingRef.current) pollingRef.current = setInterval(load, 3000); load(); } catch (e) { toast.error(e.response?.data?.detail || 'Baslatilamadi'); } };
  const handleStop = async () => { try { await globalCampaignAPI.stop(campaignId); toast.success('Durduruldu'); load(); } catch (e) { toast.error('Durdurulamadi'); } };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div>;
  if (!data) return <div className="text-center py-20 text-muted-foreground">Bulunamadi</div>;

  const counter = data.counter || {};
  const fs = data.filter_summary || null;
  const progress = data.planned_count > 0 ? Math.round((data.sent_count / data.planned_count) * 100) : 0;
  const sBadge = { created: ['Hazir','secondary'], running: ['Gonderiliyor','default'], completed: ['Tamamlandi','default'], stopped: ['Durduruldu','destructive'], failed: ['Basarisiz','destructive'] };
  const [sLabel, sVariant] = sBadge[data.status] || [data.status, 'outline'];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={onBack}><ArrowLeft className="w-5 h-5" /></Button>
          <div><h1 className="text-2xl font-heading font-bold">{data.name}</h1><div className="flex items-center gap-2 mt-1"><Badge variant={sVariant}>{sLabel}</Badge><span className="text-xs text-muted-foreground">{data.mode} | {data.domain_count} domain</span></div></div>
        </div>
        <div className="flex gap-2">
          {data.status === 'created' && <Button size="sm" onClick={handleStart}><Play className="w-4 h-4 mr-1" /> Baslat</Button>}
          {data.status === 'scheduled' && <Button size="sm" onClick={handleStart}><Play className="w-4 h-4 mr-1" /> Baslat</Button>}
          {(data.status === 'stopped' || data.status === 'failed') && data.sent_count < data.planned_count && <Button size="sm" onClick={handleStart}><Play className="w-4 h-4 mr-1" /> Devam Et</Button>}
          {data.status === 'running' && data.sent_count < data.planned_count && <Button size="sm" variant="outline" onClick={handleStart}><Play className="w-4 h-4 mr-1" /> Yeniden Baslat</Button>}
          {data.status === 'running' && <Button variant="destructive" size="sm" onClick={handleStop}><Square className="w-4 h-4 mr-1" /> Durdur</Button>}
        </div>
      </div>

      <div className="rounded-lg bg-card p-4">
        <div className="flex justify-between mb-2"><span className="text-sm font-medium">Ilerleme</span><span className="text-sm text-muted-foreground">{data.sent_count}/{data.planned_count}</span></div>
        <Progress value={progress} className="h-3" />
        {(data.status === 'running' || data.rate_per_hour > 0) && (
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-3 pt-3 border-t border-border text-sm" data-testid="live-rate-row">
            <span className="flex items-center gap-1.5">
              <Gauge className="w-4 h-4 text-green-500" />
              <span className="text-muted-foreground">Canli Hiz:</span>
              <span className={`w-2 h-2 rounded-full ${data.status === 'running' ? 'bg-green-500 animate-pulse' : 'bg-muted-foreground'}`}></span>
              <span className="font-bold text-green-500" data-testid="live-rate-value">
                {(data.rate_per_hour || 0).toLocaleString('tr-TR')} mail/saat
              </span>
              <span className="text-xs text-muted-foreground">({(data.rate_per_minute || 0).toLocaleString('tr-TR')}/dk)</span>
            </span>
            {data.eta_seconds != null && (
              <span className="flex items-center gap-1.5 text-muted-foreground">
                <Clock className="w-4 h-4" /> Tahmini kalan:
                <span className="font-medium text-foreground" data-testid="eta-value">{formatETA(data.eta_seconds)}</span>
              </span>
            )}
            <span className="text-xs text-muted-foreground ml-auto">
              Hedef: {((data.send_rate_per_minute || 6) * 60).toLocaleString('tr-TR')} mail/saat
            </span>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
        {[
          { label: 'Gonderildi', value: data.sent_count, color: 'text-green-500', icon: CheckCircle },
          { label: 'Basarisiz', value: data.failed_count || 0, color: 'text-red-400', icon: XCircle },
          { label: 'Engellendi', value: data.suppressed_count || 0, color: 'text-amber-500', icon: Shield },
          { label: 'Blocked', value: counter.blocked || 0, color: 'text-orange-400', icon: XCircle },
          { label: 'Dupe Block', value: counter.duplicates_blocked || 0, color: 'text-purple-400', icon: Users },
        ].map((stat, i) => (
          <div key={i} className="rounded-lg bg-card p-4 text-center">
            <stat.icon className={`w-5 h-5 mx-auto mb-1 ${stat.color}`} />
            <p className="text-2xl font-bold">{stat.value}</p>
            <p className="text-xs text-muted-foreground">{stat.label}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card><CardHeader className="pb-3"><CardTitle className="text-base">Detaylar</CardTitle></CardHeader><CardContent className="space-y-2 text-sm">
          {[['Mod', data.mode], ['Warmup', data.warmup_mode ? 'Evet' : 'Hayir'], ['Domain', data.domain_count], ['Template', data.template_count], ['Round', data.current_round], ['Hiz', `${data.send_rate_per_minute || 6}/dk`], ['Paralel', `${data.parallel_count || 1}x`]].map(([k,v],i) => <div key={i} className="flex justify-between"><span className="text-muted-foreground">{k}</span><span>{v}</span></div>)}
          {data.started_at && <div className="flex justify-between"><span className="text-muted-foreground">Basladi</span><span>{new Date(data.started_at).toLocaleString('tr-TR')}</span></div>}
          {data.completed_at && <div className="flex justify-between"><span className="text-muted-foreground">Bitti</span><span>{new Date(data.completed_at).toLocaleString('tr-TR')}</span></div>}
        </CardContent></Card>

        {fs ? (
          <Card><CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><Shield className="w-4 h-4 text-amber-500" /> Liste Filtreleme Özeti</CardTitle></CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex justify-between font-medium"><span>Orijinal liste</span><span>{(fs.original || 0).toLocaleString('tr-TR')}</span></div>
            <div className="border-t border-border pt-2 space-y-1.5">
              {fs.invalid_removed > 0 && <div className="flex justify-between text-red-400"><span>↳ Geçersiz format kaldırıldı</span><span>-{fs.invalid_removed.toLocaleString('tr-TR')}</span></div>}
              {fs.duplicate_removed > 0 && <div className="flex justify-between text-purple-400"><span>↳ Duplicate kaldırıldı</span><span>-{fs.duplicate_removed.toLocaleString('tr-TR')}</span></div>}
              {fs.suppressed_removed > 0 && <div className="flex justify-between text-amber-500"><span>↳ Suppression listesinde</span><span>-{fs.suppressed_removed.toLocaleString('tr-TR')}</span></div>}
            </div>
            <div className="flex justify-between font-bold text-green-500 border-t border-border pt-2">
              <span>Gönderilecek</span><span>{(fs.final || 0).toLocaleString('tr-TR')}</span>
            </div>
          </CardContent></Card>
        ) : (
          <Card><CardHeader className="pb-3"><CardTitle className="text-base">Atomic Counter</CardTitle></CardHeader><CardContent className="space-y-2 text-sm">
            {[['Actual', counter.actual || 0], ['Planned', counter.planned || 0], ['Overflow Blocked', counter.blocked || 0], ['Dupe Blocked', counter.duplicates_blocked || 0]].map(([k,v],i) => <div key={i} className="flex justify-between"><span className="text-muted-foreground">{k}</span><span className="font-mono">{v}</span></div>)}
            {data.status === 'completed' && counter.actual === counter.planned && <div className="flex justify-between"><span className="text-muted-foreground">Exact Match</span><span className="text-green-500 font-bold">EVET</span></div>}
          </CardContent></Card>
        )}
      </div>

      {/* Atomic Counter (when filter_summary is shown, move counter below) */}
      {fs && (
        <Card><CardHeader className="pb-3"><CardTitle className="text-base">Atomic Counter</CardTitle></CardHeader><CardContent className="space-y-2 text-sm">
          {[['Actual', counter.actual || 0], ['Planned', counter.planned || 0], ['Overflow Blocked', counter.blocked || 0], ['Dupe Blocked', counter.duplicates_blocked || 0]].map(([k,v],i) => <div key={i} className="flex justify-between"><span className="text-muted-foreground">{k}</span><span className="font-mono">{v}</span></div>)}
          {data.status === 'completed' && counter.actual === counter.planned && <div className="flex justify-between"><span className="text-muted-foreground">Exact Match</span><span className="text-green-500 font-bold">EVET</span></div>}
        </CardContent></Card>
      )}
    </div>
  );
}

// === MAIL LISTS VIEW ===
function MailListsView({ onBack }) {
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { mailListAPI.list().then(r => setLists(r.data)).finally(() => setLoading(false)); }, []);

  const handleDelete = async (id) => { await mailListAPI.delete(id); toast.success('Silindi'); setLists(lists.filter(l => l.id !== id)); };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin" /></div>;
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3"><Button variant="ghost" size="icon" onClick={onBack}><ArrowLeft className="w-5 h-5" /></Button><h1 className="text-2xl font-heading font-bold">Mail Listeleri</h1></div>
      {lists.length === 0 ? <Card><CardContent className="text-center py-12 text-muted-foreground">Henuz liste yok. Kampanya olustururken CSV yukleyebilirsiniz.</CardContent></Card> :
        <div className="space-y-3">{lists.map(l => (
          <Card key={l.id}><CardContent className="flex items-center justify-between py-4">
            <div><p className="font-medium">{l.name}</p><div className="flex gap-3 mt-1 text-xs text-muted-foreground"><span>{l.total_count} kisi</span><span className="text-green-500">{l.valid_count} gecerli</span><span className="text-amber-500">{l.suppressed_count} engelli</span><span>{l.source}</span></div></div>
            <Button variant="ghost" size="icon" className="text-destructive" onClick={() => handleDelete(l.id)}><Trash2 className="w-4 h-4" /></Button>
          </CardContent></Card>
        ))}</div>
      }
    </div>
  );
}

// === MAIN PAGE ===
export default function GlobalMailPage() {
  const [view, setView] = useState('list');
  const [selectedId, setSelectedId] = useState(null);

  return (
    <div>
      {view === 'list' && <GCListView onSelect={id => { setSelectedId(id); setView('detail'); }} onCreate={() => setView('create')} onLists={() => setView('lists')} />}
      {view === 'create' && <GCCreateView onCreated={id => { setSelectedId(id); setView('detail'); }} onBack={() => setView('list')} />}
      {view === 'detail' && selectedId && <GCDetailView campaignId={selectedId} onBack={() => { setSelectedId(null); setView('list'); }} />}
      {view === 'lists' && <MailListsView onBack={() => setView('list')} />}
    </div>
  );
}
